import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, ShieldAlert, AlertTriangle, Activity, Lock } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";

const SEV_COLOR = { info: "blue", warning: "amber", critical: "red" };
const CRITICAL_TYPES = new Set(["TOKEN_REUSE", "ACCOUNT_LOCKED"]);

function StatCard({ icon: Icon, label, value, tone = "teal", testid }) {
  const tones = {
    teal: "text-teal-600 bg-teal-50 dark:bg-teal-500/10",
    red: "text-red-600 bg-red-50 dark:bg-red-500/10",
    amber: "text-amber-600 bg-amber-50 dark:bg-amber-500/10",
    blue: "text-sky-600 bg-sky-50 dark:bg-sky-500/10",
  };
  return (
    <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-semibold text-foreground leading-none">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function SecurityEventsPage() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState({ total: 0, critical: 0, bySeverity: {}, byType: {} });
  const [chain, setChain] = useState(null);
  const [filters, setFilters] = useState({ eventType: "", severity: "" });
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.eventType) params.set("eventType", filters.eventType);
    if (filters.severity) params.set("severity", filters.severity);
    params.set("size", "100");
    try {
      const [ev, st] = await Promise.all([
        api.get(`/v1/security/events?${params}`),
        api.get(`/v1/security/events/stats`),
      ]);
      setData(ev.data.data);
      setStats(st.data.data);
      setAccessDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setAccessDenied(true);
      setData({ items: [], total: 0 });
      setStats({ total: 0, critical: 0, bySeverity: {}, byType: {} });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const verifyChain = async () => {
    setChain({ loading: true });
    try {
      const res = await api.get(`/v1/security/audit/verify`);
      setChain(res.data.data);
    } catch {
      setChain({ valid: false, error: true });
    }
  };

  if (accessDenied) {
    return (
      <div data-testid="security-events-page">
        <PageHeader title="Security Events" subtitle="Append-only security activity stream" />
        <div className="bg-card rounded-xl border border-border p-10 text-center" data-testid="security-access-denied">
          <Lock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <div className="text-lg font-semibold text-foreground">Access restricted</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            You do not have permission to view Security Events. This area requires the
            <span className="font-mono text-xs mx-1 px-1.5 py-0.5 rounded bg-muted">SECURITY.VIEW</span>
            permission. Contact your administrator if you need access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="security-events-page">
      <PageHeader title="Security Events" subtitle={`Append-only security activity stream · ${stats.total} events`} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard icon={Activity} label="Total events" value={stats.total} tone="teal" testid="stat-total" />
        <StatCard icon={ShieldAlert} label="Critical" value={stats.critical || 0} tone="red" testid="stat-critical" />
        <StatCard icon={AlertTriangle} label="Warnings" value={stats.bySeverity?.warning || 0} tone="amber" testid="stat-warning" />
        <StatCard icon={ShieldCheck} label="Info" value={stats.bySeverity?.info || 0} tone="blue" testid="stat-info" />
      </div>

      <div className="bg-card rounded-xl border border-border p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="audit-chain-panel">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-teal-600" />
          <span className="text-sm font-medium text-foreground">Audit Integrity</span>
        </div>
        <button onClick={verifyChain} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-1.5 transition-colors" data-testid="verify-chain-btn">
          Verify hash chain
        </button>
        {chain && !chain.loading && (
          <span data-testid="chain-result">
            {chain.valid
              ? <Badge color="green">Chain intact · {chain.checked} records verified</Badge>
              : <Badge color="red">Tampering detected{chain.brokenAt ? ` at ${chain.brokenAt.id?.slice(0, 8)}` : ""}</Badge>}
          </span>
        )}
        {chain?.loading && <span className="text-sm text-muted-foreground">Verifying…</span>}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <input placeholder="Event type (e.g. LOGIN_FAILURE)" value={filters.eventType}
          onChange={(e) => setFilters({ ...filters, eventType: e.target.value.toUpperCase() })}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground" data-testid="filter-event-type" />
        <select value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground" data-testid="filter-severity">
          <option value="">All severities</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      <DataTable testid="security-events-table" rows={data.items} loading={loading}
        columns={[
          { key: "createdOn", header: "Time", render: (e) => (e.createdOn || "").slice(0, 19).replace("T", " ") },
          { key: "eventType", header: "Event", render: (e) => <Badge color={CRITICAL_TYPES.has(e.eventType) ? "red" : "teal"}>{e.eventType}</Badge> },
          { key: "severity", header: "Severity", render: (e) => <Badge color={SEV_COLOR[e.severity] || "slate"}>{e.severity}</Badge> },
          { key: "email", header: "Actor", render: (e) => e.email || "—" },
          { key: "ip", header: "IP", render: (e) => <span className="font-mono text-xs">{e.ip || "—"}</span> },
          { key: "detail", header: "Detail", render: (e) => <span className="text-xs text-muted-foreground">{e.detail && Object.keys(e.detail).length ? JSON.stringify(e.detail) : "—"}</span> },
        ]} />
    </div>
  );
}
